import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Loader2, Save, DoorOpen } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";

interface DaySchedule {
  dayOfWeek: number;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
}

interface AppointmentRoomSummary {
  id: string;
  name: string;
  color: string;
}

const DAY_NAMES = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function defaultSchedule(): DaySchedule[] {
  return Array.from({ length: 7 }, (_, dayOfWeek) => ({
    dayOfWeek,
    isOpen: dayOfWeek !== 0,
    openTime: dayOfWeek === 0 ? null : "09:00",
    closeTime: dayOfWeek === 0 ? null : "20:00",
  }));
}

export default function AppointmentRoomSchedule() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();

  const [room, setRoom] = useState<AppointmentRoomSummary | null>(null);
  const [schedule, setSchedule] = useState<DaySchedule[]>(defaultSchedule());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [roomRes, scheduleRes] = await Promise.all([
        apiClient.get(`/appointment-rooms/${id}`),
        apiClient.get(`/appointment-rooms/${id}/schedule`),
      ]);
      setRoom(roomRes.data.data);
      const loaded: DaySchedule[] =
        scheduleRes.data.data?.days ?? scheduleRes.data.data;
      if (loaded && loaded.length > 0) {
        // Merge con defaults (puede que no tenga los 7 días aún)
        const merged = defaultSchedule().map((def) => {
          const found = loaded.find((d) => d.dayOfWeek === def.dayOfWeek);
          return found ?? def;
        });
        setSchedule(merged);
      }
    } catch {
      showToast("Error al cargar el horario de la sala", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateDay = (dayOfWeek: number, patch: Partial<DaySchedule>) => {
    setSchedule((prev) =>
      prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)),
    );
    setDirty(true);
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await apiClient.put(`/appointment-rooms/${id}/schedule`, {
        days: schedule,
      });
      showToast("Horario guardado correctamente");
      setDirty(false);
    } catch {
      showToast("Error al guardar el horario", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={() => navigate("/appointment-rooms")}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a salas
        </button>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {room && (
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: room.color }}
              >
                <DoorOpen className="w-5 h-5" />
              </div>
            )}
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                Horario — {room?.name ?? "Sala"}
              </h1>
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Horario de apertura semanal de la sala
              </p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar
          </button>
        </div>
      </div>

      {/* Grid de días */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900 text-sm md:text-base">
            Horario semanal
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Define las horas de apertura y cierre de la sala para cada día de la
            semana
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {DAY_ORDER.map((dayOfWeek) => {
            const day = schedule.find((d) => d.dayOfWeek === dayOfWeek)!;
            if (!day) return null;
            return (
              <div
                key={dayOfWeek}
                className={`px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                  !day.isOpen ? "bg-gray-50" : ""
                }`}
              >
                {/* Nombre del día + toggle */}
                <div className="flex items-center gap-3 sm:w-40 flex-shrink-0">
                  <button
                    onClick={() =>
                      updateDay(dayOfWeek, {
                        isOpen: !day.isOpen,
                        openTime: !day.isOpen ? "09:00" : null,
                        closeTime: !day.isOpen ? "20:00" : null,
                      })
                    }
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                      day.isOpen ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        day.isOpen ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                  <span
                    className={`text-sm font-medium ${
                      day.isOpen ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {DAY_NAMES[dayOfWeek]}
                  </span>
                </div>

                {/* Horas o "cerrado" */}
                {day.isOpen ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500">Apertura</label>
                      <input
                        type="time"
                        value={day.openTime || "09:00"}
                        onChange={(e) =>
                          updateDay(dayOfWeek, { openTime: e.target.value })
                        }
                        className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <span className="text-gray-300">—</span>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500">Cierre</label>
                      <input
                        type="time"
                        value={day.closeTime || "20:00"}
                        onChange={(e) =>
                          updateDay(dayOfWeek, { closeTime: e.target.value })
                        }
                        className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 italic">Cerrado</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
